import React from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  LinearProgress,
  Divider,
  SvgIcon,
  Grid,
} from "@mui/material";
import { ReactComponent as award } from "./ImageforDashboardStudent/award.svg";
import { ReactComponent as trend } from "./ImageforDashboardStudent/trend-up.svg";
import { ReactComponent as StreakLegend } from "../../Assets/Images/Achievements/Streak_legend.svg";
import { Zap } from "lucide-react";

interface Achievement {
  label: string;
  icon: React.ReactNode;
}

interface StudyStreakCardProps {
  Dailygoal: any;
  totalDots: number;
  filledDots: number;
  handleOpen: () => void;
}

const StudyStreakCard: React.FC<StudyStreakCardProps> = ({
  Dailygoal,
  totalDots,
  filledDots,
  handleOpen,
}) => {
  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        boxShadow: "none",
      }}
    >
      {/* Title and Update Button */}
      {/* <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: "18px" }}
        >
          Study Streak
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {/* <Button
            variant="contained"
            sx={{
              backgroundColor: "#00aaff",
              textTransform: "none",
              borderRadius: 2,
              fontWeight: "bold",
              color: "white",
              "&:hover": {
                backgroundColor: "#007acc",
              },
            }}
            onClick={handleOpen}
          >
            Update Goal
          </Button> */}
      {/* </Typography>
      </Box> */}

      {/* Streak Box */}
      <Box
        sx={{
          backgroundColor: "#e5f5ff",
          mt: 2,
          borderRadius: "10px",
          textAlign: "center",
          py: 3,
        }}
      >
        <Box>
          <SvgIcon component={award} sx={{ fill: "#e5f5ff", fontSize: 25 }} />
        </Box>
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize={"20px"}
          sx={{ lineheight: "1.75rem" }}
        >
          {Dailygoal?.current_streak}-Day Streak
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#4b5563" }}>
          Goal: {Dailygoal?.daily_goal | 0} days
        </Typography>

        <Box display="flex" justifyContent="center" gap={1} mt={1}>
          {[...Array(totalDots)].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: i < filledDots ? "#009dff" : "#E0E0E0",
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Daily Goal */}
      <Box mt={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography
            fontWeight={500}
            sx={{ fontSize: "14px", marginLeft: "30px" }}
          >
            Daily Goal
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              backgroundColor: "#E6F4FF",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <SvgIcon
              component={trend}
              sx={{ fill: "white", fontSize: 30, padding: "4px" }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <LinearProgress
              variant="determinate"
              value={
                Dailygoal?.questions_attempted_today >= Dailygoal?.daily_goal
                  ? 100
                  : (Dailygoal?.questions_attempted_today /
                      Dailygoal?.daily_goal) *
                    100
              }
              sx={{
                height: 8,
                // width: "260px",
                width: "100%",
                borderRadius: 5,
                backgroundColor: "#E0E0E0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#009dff",
                },
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "12px" }}
            >
              {Dailygoal?.questions_attempted_today > Dailygoal?.daily_goal
                ? `${Dailygoal?.daily_goal}/${Dailygoal?.daily_goal} questions`
                : `${Dailygoal?.questions_attempted_today}/${Dailygoal?.daily_goal} questions`}
            </Typography>
          </Box>
        </Box>

        {/* Goal Status Message */}
        {Dailygoal?.questions_attempted_today === 0 ? (
          <Typography
            variant="caption"
            color="#ef4444"
            sx={{
              fontSize: "13px",
              display: "flex",
              mt: 0.5,
              fontWeight: 600,
              paddingLeft: "35px",
              // justifyContent: "center",
              // alignItems: "center",
            }}
          >
            🚀 Start now! Complete your daily goal today!
          </Typography>
        ) : Dailygoal?.questions_attempted_today >= Dailygoal?.daily_goal ? (
          <Typography
            variant="caption"
            color="success.main"
            sx={{
              fontSize: "13px",
              display: "block",
              mt: 0.5,
              fontWeight: 600,
            }}
          >
            🏆 Awesome! You answered {Dailygoal?.questions_attempted_today}{" "}
            questions (
            {Math.round(
              (Dailygoal?.questions_attempted_today / Dailygoal?.daily_goal) *
                100
            )}
            % of your goal)!
          </Typography>
        ) : (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{
              fontSize: "13px",
              display: "block",
              mt: 0.5,
              fontWeight: 600,
            }}
          >
            ⏱️Keep going! You have completed{" "}
            {Dailygoal?.questions_attempted_today} questions. That's&nbsp;
            {Math.round(
              (Dailygoal?.questions_attempted_today / Dailygoal?.daily_goal) *
                100
            )}
            % of your daily target.
          </Typography>
        )}
      </Box>

      <Divider variant="middle" sx={{ margin: "15px 0px" }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        <Box
          sx={{
            mt: 3,
            bgcolor: "#f9fafb",
            width: "100%",
            borderRadius: "10px",
            p: "12px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 0.5,
              color: "#009DFF",
              fontSize: "16px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <StreakLegend
              style={{
                stroke: "#009DFF",
                strokeWidth: 1.0,
                color: "#009DFF",
                fill: "transparent",
              }}
            />
            {Dailygoal?.current_streak || 0} Days
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#b5b6b6ff" }}>
            Current Streak
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 3,
            bgcolor: "#f9fafb",
            width: "100%",
            borderRadius: "10px",
            p: "12px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 0.5,
              color: "Black",
              fontSize: "16px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Zap
              style={{
                width: "20px",
                height: "20px",
                color: "black",
                fill: "transparent",
              }}
            />
            {Dailygoal?.longest_streak || 0} Days
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#b5b6b6ff" }}>
            Longest Streak
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default StudyStreakCard;
